import { i18n, t } from '@/utils/i18n'
import { cn } from '@/utils/cn'
import { navigationMenu, type SubMenuItem } from '@/config/navigation'
import { parsePath, withLangPrefix } from '@/utils/lang'
import { trackEvent } from '@/utils/analytics'

export function Header(): HTMLElement {
  const header = document.createElement('header')
  header.className = 'fixed top-4 left-4 right-4 z-50 transition-all duration-300 rounded-2xl bg-white/20 backdrop-blur-md border border-white/20'

  const container = document.createElement('div')
  container.className = 'mx-auto px-4 sm:px-6 lg:px-8'

  const nav = document.createElement('nav')
  nav.className = 'flex items-center justify-between h-16'

  // Logo
  const logoLink = document.createElement('a')
  logoLink.href = '/'
  logoLink.className = 'flex items-center group'
  logoLink.addEventListener('click', (e) => {
    e.preventDefault()
    window.dispatchEvent(new CustomEvent('navigate', { detail: '/' }))
  })

  const logoImg = document.createElement('img')
  logoImg.src = '/logo/GIGAette_logo.png'
  logoImg.alt = 'GIGAette'
  logoImg.className = 'h-6 sm:h-7 lg:h-8 transition-all duration-300 group-hover:scale-105'

  logoLink.appendChild(logoImg)

  // Desktop Navigation
  const navLinks = document.createElement('div')
  navLinks.className = 'hidden lg:flex items-center space-x-0'

  // Build navigation items from config
  navigationMenu.forEach(item => {
    if (item.submenu) {
      // Item with dropdown
      const dropdown = createDropdown(item.key, item.href, item.submenu)
      navLinks.appendChild(dropdown)
    } else {
      // Regular link
      const link = createNavLink(item.key, item.href)
      navLinks.appendChild(link)
    }
  })

  // Create regular nav link with underline effect
  function createNavLink(key: string, href: string): HTMLElement {
    const a = document.createElement('a')
    a.href = href
    a.textContent = t(key)
    a.className = 'relative text-white hover:text-orange-500 transition-colors duration-300 font-medium text-base tracking-wide py-2 px-3 nav-link group'

    const underline = document.createElement('span')
    underline.className = 'absolute bottom-1 left-3 right-3 h-0.5 bg-orange-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left'
    a.appendChild(underline)

    a.addEventListener('click', (e) => {
      e.preventDefault()
      window.dispatchEvent(new CustomEvent('navigate', { detail: href }))
    })

    return a
  }

  // Create dropdown menu
  function createDropdown(key: string, href: string, submenu: SubMenuItem[]): HTMLElement {
    const wrapper = document.createElement('div')
    wrapper.className = 'relative group'

    // Main link
    const mainLink = document.createElement('a')
    mainLink.href = href
    mainLink.className = 'relative text-white hover:text-orange-500 transition-colors duration-300 font-medium text-base tracking-wide py-2 px-3 nav-link flex items-center gap-1 cursor-pointer'

    const textSpan = document.createElement('span')
    textSpan.textContent = t(key)
    mainLink.appendChild(textSpan)

    const arrow = document.createElement('span')
    arrow.innerHTML = '<svg class="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>'
    mainLink.appendChild(arrow)

    // Underline effect
    const underline = document.createElement('span')
    underline.className = 'absolute bottom-1 left-3 right-3 h-0.5 bg-orange-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left'
    mainLink.appendChild(underline)

    mainLink.addEventListener('click', (e) => {
      e.preventDefault()
      window.dispatchEvent(new CustomEvent('navigate', { detail: href }))
    })

    // Dropdown panel
    const dropdownPanel = document.createElement('div')
    dropdownPanel.className = cn(
      'absolute top-full left-0 mt-2 min-w-[200px] py-2 rounded-xl bg-neutral-900/90 backdrop-blur-md border border-white/10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 dropdown-panel shadow-xl',
      key === 'nav.projects' && 'min-w-[260px] w-max'
    )

    submenu.forEach(subItem => {
      const subLink = document.createElement('a')
      subLink.href = subItem.href
      subLink.textContent = subItem.label || t(subItem.key!)
      subLink.className = 'relative block px-4 py-2 text-sm text-white/90 hover:text-orange-500 transition-colors duration-300 font-medium group/sub submenu-link'

      // Underline effect for submenu
      const subUnderline = document.createElement('span')
      subUnderline.className = 'absolute bottom-1 left-4 right-4 h-0.5 bg-orange-500 transform scale-x-0 group-hover/sub:scale-x-100 transition-transform duration-300 origin-left'
      subLink.appendChild(subUnderline)

      subLink.addEventListener('click', (e) => {
        e.preventDefault()
        window.dispatchEvent(new CustomEvent('navigate', { detail: subItem.href }))
      })

      dropdownPanel.appendChild(subLink)
    })

    wrapper.appendChild(mainLink)
    wrapper.appendChild(dropdownPanel)

    return wrapper
  }

  // Language Toggle
  const langToggle = document.createElement('div')
  langToggle.className = 'flex items-center space-x-1 ml-8 bg-white/30 rounded-full p-1'

  const createLangButton = (lang: 'en' | 'ko', label: string) => {
    const button = document.createElement('button')
    button.textContent = label
    button.className = cn(
      'px-3 py-1 rounded-full text-xs font-bold transition-all duration-300',
      i18n.getCurrentLanguage() === lang
        ? 'bg-white/90 text-orange-600 shadow-sm'
        : 'text-white/70 hover:text-white'
    )
    button.addEventListener('click', () => {
      // Switch language by navigating to the same logical page in the target
      // language (full load; bootstrapLang re-reads the language from the URL).
      const current = i18n.getCurrentLanguage()
      if (lang !== current) {
        // gtag sends via sendBeacon, so this survives the imminent full reload.
        trackEvent('language_switch', { from: current, to: lang })
      }
      const { logicalPath } = parsePath(window.location.pathname)
      window.location.href = withLangPrefix(logicalPath, lang)
    })
    return button
  }

  langToggle.appendChild(createLangButton('en', 'EN'))
  langToggle.appendChild(createLangButton('ko', 'KR'))

  // Mobile Menu Button
  // Desktop nav is `hidden lg:flex`, so below 1024px this button is the ONLY
  // way to reach any page — it must always have a working panel behind it.
  const ICON_BURGER =
    '<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/></svg>'
  const ICON_CLOSE =
    '<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>'

  const mobileMenuButton = document.createElement('button')
  mobileMenuButton.type = 'button'
  mobileMenuButton.className = 'lg:hidden p-2 text-white hover:text-orange-400 transition-colors duration-300 mobile-menu-btn'
  mobileMenuButton.innerHTML = ICON_BURGER
  mobileMenuButton.setAttribute('aria-label', t('nav.openMenu'))
  mobileMenuButton.setAttribute('aria-expanded', 'false')
  mobileMenuButton.setAttribute('aria-controls', 'mobile-menu')

  // Mobile menu panel — drops out of the header card, scrolls if it overflows.
  const mobilePanel = document.createElement('div')
  mobilePanel.id = 'mobile-menu'
  mobilePanel.className =
    'lg:hidden overflow-hidden transition-[max-height] duration-300 ease-in-out ' +
    'rounded-b-2xl bg-white/95 backdrop-blur-md'
  mobilePanel.style.maxHeight = '0px'
  mobilePanel.setAttribute('aria-hidden', 'true')

  const mobileList = document.createElement('div')
  mobileList.className =
    'py-3 px-2 sm:px-4 border-t border-slate-200 max-h-[70vh] overflow-y-auto overscroll-contain'
  mobilePanel.appendChild(mobileList)

  let menuOpen = false

  const setMenu = (open: boolean) => {
    menuOpen = open
    mobileMenuButton.innerHTML = open ? ICON_CLOSE : ICON_BURGER
    mobileMenuButton.setAttribute('aria-expanded', String(open))
    mobileMenuButton.setAttribute('aria-label', t(open ? 'nav.closeMenu' : 'nav.openMenu'))
    mobilePanel.setAttribute('aria-hidden', String(!open))

    if (open) {
      // Animate to the measured height, then release the cap. Without the
      // release the panel keeps the height it had when it opened, and a
      // submenu expanded afterwards is clipped — its own transition has not
      // run yet when the handler measures, so re-measuring there reads a
      // stale value.
      mobilePanel.style.maxHeight = `${mobileList.offsetHeight}px`
      window.setTimeout(() => { if (menuOpen) mobilePanel.style.maxHeight = 'none' }, 320)
    } else {
      // Coming back from `none`, pin the current height for one frame so the
      // collapse has something to animate from.
      mobilePanel.style.maxHeight = `${mobilePanel.scrollHeight}px`
      requestAnimationFrame(() => {
        requestAnimationFrame(() => { if (!menuOpen) mobilePanel.style.maxHeight = '0px' })
      })
    }
    // The panel scrolls internally; lock the page behind it.
    document.body.style.overflow = open ? 'hidden' : ''
  }

  const closeMenu = () => { if (menuOpen) setMenu(false) }

  const goto = (href: string) => {
    closeMenu()
    window.dispatchEvent(new CustomEvent('navigate', { detail: href }))
  }

  // Build the mobile list from the same config the desktop nav uses.
  navigationMenu.forEach((item) => {
    const label = t(item.key)

    if (!item.submenu) {
      const link = document.createElement('a')
      link.href = item.href
      link.textContent = label
      link.className =
        'block px-4 py-3 rounded-xl text-base font-semibold text-slate-800 hover:bg-slate-100 transition-colors'
      link.addEventListener('click', (e) => { e.preventDefault(); goto(item.href) })
      mobileList.appendChild(link)
      return
    }

    // Accordion: the row navigates, the chevron expands. Keeping them separate
    // means the section landing page stays reachable in one tap.
    const group = document.createElement('div')

    const row = document.createElement('div')
    row.className = 'flex items-center rounded-xl hover:bg-slate-100 transition-colors'

    const mainLink = document.createElement('a')
    mainLink.href = item.href
    mainLink.textContent = label
    mainLink.className = 'flex-1 px-4 py-3 text-base font-semibold text-slate-800'
    mainLink.addEventListener('click', (e) => { e.preventDefault(); goto(item.href) })

    const expandBtn = document.createElement('button')
    expandBtn.type = 'button'
    expandBtn.className = 'px-4 py-3 text-slate-500 hover:text-orange-600 transition-colors'
    expandBtn.setAttribute('aria-expanded', 'false')
    expandBtn.setAttribute('aria-label', `${label} — ${t('nav.expandSection')}`)
    expandBtn.innerHTML =
      '<svg class="w-5 h-5 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>'

    const sub = document.createElement('div')
    sub.className = 'overflow-hidden transition-[max-height] duration-200 ease-in-out pl-3'
    sub.style.maxHeight = '0px'

    item.submenu.forEach((subItem: SubMenuItem) => {
      const subLink = document.createElement('a')
      subLink.href = subItem.href
      subLink.textContent = subItem.label || t(subItem.key!)
      subLink.className =
        'block px-4 py-2.5 rounded-lg text-sm text-slate-600 hover:text-orange-600 hover:bg-slate-50 transition-colors'
      subLink.addEventListener('click', (e) => { e.preventDefault(); goto(subItem.href) })
      sub.appendChild(subLink)
    })

    expandBtn.addEventListener('click', () => {
      const open = sub.style.maxHeight !== '0px'
      sub.style.maxHeight = open ? '0px' : `${sub.scrollHeight}px`
      expandBtn.setAttribute('aria-expanded', String(!open))
      expandBtn.querySelector('svg')?.classList.toggle('rotate-180', !open)
      // No panel resize needed: while open its max-height is `none`, so it
      // follows the list, which caps itself at 70vh and scrolls.
    })

    row.appendChild(mainLink)
    row.appendChild(expandBtn)
    group.appendChild(row)
    group.appendChild(sub)
    mobileList.appendChild(group)
  })

  mobileMenuButton.addEventListener('click', () => setMenu(!menuOpen))

  // Close on Escape, on tapping outside the header, and when the viewport
  // grows past the lg breakpoint (where the desktop nav takes over and the
  // panel would otherwise stay open with the body still scroll-locked).
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeMenu() })
  document.addEventListener('click', (e) => {
    if (!menuOpen) return
    // Use composedPath(), not contains(e.target): opening the menu swaps the
    // button's innerHTML for the close icon, which detaches the <svg><path>
    // that was clicked. By the time this bubble listener runs, contains() on
    // that now-orphaned node returns false and the menu closes itself the
    // instant it opens. composedPath is captured at dispatch, before the swap.
    const path = typeof e.composedPath === 'function' ? e.composedPath() : []
    const insideHeader = path.length ? path.includes(header) : header.contains(e.target as Node)
    if (!insideHeader) closeMenu()
  })
  window.addEventListener('resize', () => {
    if (menuOpen && window.innerWidth >= 1024) closeMenu()
  })

  // Assemble nav
  nav.appendChild(logoLink)
  nav.appendChild(navLinks)
  const rightSection = document.createElement('div')
  rightSection.className = 'flex items-center'
  rightSection.appendChild(langToggle)
  rightSection.appendChild(mobileMenuButton)
  nav.appendChild(rightSection)

  container.appendChild(nav)
  header.appendChild(container)
  header.appendChild(mobilePanel)

  // Scroll effect with dynamic color change
  const updateHeaderStyle = () => {
    const isHomePage = window.location.pathname === '/'
    const heroHeight = isHomePage
      ? window.innerHeight - 100
      : window.innerHeight * 0.8 - 100
    const navLinkElements = header.querySelectorAll('.nav-link')
    const submenuLinks = header.querySelectorAll('.submenu-link')
    const dropdownPanels = header.querySelectorAll('.dropdown-panel')
    const langButtons = langToggle.querySelectorAll('button')

    if (window.scrollY > heroHeight) {
      // Past hero section: dark text + opaque background
      header.classList.add('header-scrolled')
      header.classList.remove('bg-white/20', 'border-white/20')
      header.classList.add('bg-white/95', 'border-slate-200', 'shadow-glass')

      navLinkElements.forEach(link => {
        link.classList.remove('text-white')
        link.classList.add('text-slate-700')
      })

      submenuLinks.forEach(link => {
        link.classList.remove('text-white/90')
        link.classList.add('text-slate-600')
      })

      dropdownPanels.forEach(panel => {
        panel.classList.remove('bg-neutral-900/90', 'border-white/10')
        panel.classList.add('bg-white/95', 'border-neutral-200', 'shadow-lg')
      })

      langToggle.classList.remove('bg-white/30')
      langToggle.classList.add('bg-slate-100')
      langButtons.forEach(btn => {
        if (!btn.classList.contains('bg-white/90')) {
          btn.classList.remove('text-white/70', 'hover:text-white')
          btn.classList.add('text-slate-500', 'hover:text-slate-700')
        }
      })

      logoImg.style.filter = 'none'

      mobileMenuButton.classList.remove('text-white', 'hover:text-orange-400')
      mobileMenuButton.classList.add('text-slate-700', 'hover:text-orange-600')
    } else {
      // In hero section: white text + transparent background
      header.classList.remove('header-scrolled')
      header.classList.add('bg-white/20', 'border-white/20')
      header.classList.remove('bg-white/95', 'border-slate-200', 'shadow-glass')

      navLinkElements.forEach(link => {
        link.classList.remove('text-slate-700')
        link.classList.add('text-white')
      })

      submenuLinks.forEach(link => {
        link.classList.remove('text-slate-600')
        link.classList.add('text-white/90')
      })

      dropdownPanels.forEach(panel => {
        panel.classList.remove('bg-white/95', 'border-neutral-200', 'shadow-lg')
        panel.classList.add('bg-neutral-900/90', 'border-white/10')
      })

      langToggle.classList.add('bg-white/30')
      langToggle.classList.remove('bg-slate-100')
      langButtons.forEach(btn => {
        if (!btn.classList.contains('bg-white/90')) {
          btn.classList.add('text-white/70', 'hover:text-white')
          btn.classList.remove('text-slate-500', 'hover:text-slate-700')
        }
      })

      logoImg.style.filter = 'brightness(1.1)'

      mobileMenuButton.classList.add('text-white', 'hover:text-orange-400')
      mobileMenuButton.classList.remove('text-slate-700', 'hover:text-orange-600')
    }

    // Position change on any scroll
    if (window.scrollY > 10) {
      header.classList.add('shadow-glass-hover')
      header.classList.remove('top-4', 'left-4', 'right-4', 'rounded-2xl')
      header.classList.add('top-0', 'left-0', 'right-0', 'rounded-none')
    } else {
      header.classList.remove('shadow-glass-hover')
      header.classList.add('top-4', 'left-4', 'right-4', 'rounded-2xl')
      header.classList.remove('top-0', 'left-0', 'right-0', 'rounded-none')
    }
  }

  window.addEventListener('scroll', updateHeaderStyle)
  updateHeaderStyle()

  return header
}
